import { ref, computed } from 'vue'
import i18n, { LANGUAGE_MAP, CURRENCY_SYMBOLS } from '@/i18n'
import { languageIdBus } from './languageIdBus'

/**
 * Current language code (e.g., 'fr', 'tr', 'en', 'de')
 * Set from backend config_language setting
 */
export const currentLanguage = ref('fr')

/**
 * Supported languages for this store
 * Set from backend selectedLanguages setting
 * e.g., { tr: true, fr: false, en: false }
 */
export const supportedLanguages = ref<Record<string, boolean>>({})

/**
 * Currency code (e.g., 'EUR', 'TRY', 'USD')
 * Set from backend config_currency setting
 */
export const currentCurrency = ref('EUR')

/**
 * Currency info resolved from backend (storeData.currency)
 * Fields: code, title, symbol_left, symbol_right, decimal_place
 * Falls back to CURRENCY_SYMBOLS map when backend doesn't provide it.
 */
export const currencyInfo = ref<{
    code?: string
    title?: string
    symbol_left?: string | null
    symbol_right?: string | null
    decimal_place?: number
} | null>(null)

/**
 * Currency symbol — prefers backend data, falls back to local map
 */
export const currencySymbol = computed(() => {
    const info = currencyInfo.value
    if (info) {
        return info.symbol_left || info.symbol_right || ''
    }
    const fallback = CURRENCY_SYMBOLS[currentCurrency.value]
    return fallback?.symbol ?? '€'
})

/**
 * Currency position — locale convention wins over DB data for known currencies.
 * OpenCart's 8i9_currency admin often puts the symbol in `symbol_left` regardless
 * of typographic convention (e.g. TRY with symbol_left='₺' → wrong for Turkish).
 * For currencies in CURRENCY_SYMBOLS we trust the code-defined position; for
 * anything else we fall back to whichever DB column is populated.
 */
export const currencyPosition = computed(() => {
    // 1) Well-known currency → use locale convention (TRY=right, EUR=right, USD=left, …)
    const known = CURRENCY_SYMBOLS[currentCurrency.value]
    if (known) return known.position

    // 2) Unknown currency → respect admin's DB choice
    const info = currencyInfo.value
    if (info) {
        if (info.symbol_right) return 'right'
        if (info.symbol_left) return 'left'
    }

    return 'right'
})

/**
 * Decimal places (default 2)
 */
export const currencyDecimals = computed(() => {
    return currencyInfo.value?.decimal_place ?? 2
})

/**
 * Format price with currency symbol.
 * Delegates to formatCurrencyForLocale so decimal/thousand separators follow
 * the active UI locale (e.g. "8,50 ₺" in tr-TR instead of "8.50 ₺").
 */
export function formatPrice(price: string | number): string {
    return formatCurrencyForLocale(price)
}

/**
 * Map our internal language code to a browser BCP-47 locale for Intl APIs.
 */
const DATE_LOCALE_MAP: Record<string, string> = {
    tr: 'tr-TR',
    en: 'en-GB',
    fr: 'fr-FR',
    de: 'de-DE'
}

/**
 * Format a date using the active language's locale.
 * Returns an empty string for invalid dates.
 */
export function formatDateForLocale(
    date: Date | string | number | null | undefined,
    options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    }
): string {
    if (!date) return ''
    const d = date instanceof Date ? date : new Date(date)
    if (isNaN(d.getTime())) return ''
    const locale = DATE_LOCALE_MAP[currentLanguage.value] ?? 'fr-FR'
    return d.toLocaleDateString(locale, options)
}

/**
 * Format a currency amount using the active language's locale and the store's
 * configured currency (falls back to EUR). Number part (decimal + thousand
 * separators) is handled by Intl.NumberFormat; symbol + position come from
 * currencySymbol/currencyPosition so we can override CLDR when it conflicts
 * with local typographic convention (e.g. tr-TR CLDR puts ₺ before the
 * number but Turkish usage puts it after).
 */
export function formatCurrencyForLocale(
    price: number | string,
    currencyCode?: string
): string {
    const num = typeof price === 'string' ? parseFloat(price) : price
    if (!isFinite(num)) return ''

    const locale = DATE_LOCALE_MAP[currentLanguage.value] ?? 'fr-FR'
    const decimals = currencyDecimals.value

    // Number-only formatting — gives us "7,00" in tr-TR, "7.00" in en-GB, etc.
    const formattedNumber = new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(num)

    let symbol: string
    let position: 'left' | 'right'

    if (currencyCode) {
        // Explicit currency override — consult the well-known map
        const known = CURRENCY_SYMBOLS[currencyCode]
        symbol = known?.symbol ?? currencyCode
        position = known?.position ?? 'right'
    } else {
        // Store's active currency — use reactive source of truth
        symbol = currencySymbol.value
        position = currencyPosition.value
    }

    // Left-positioned symbols (USD/GBP) have no space: "$7.00".
    // Right-positioned (TRY/EUR) use non-breaking space (U+00A0) so the symbol
    // never wraps onto a new line in narrow containers like basket rows.
    return position === 'left'
        ? `${symbol}${formattedNumber}`
        : `${formattedNumber}\u00A0${symbol}`
}

/**
 * List of active languages for this store (for language selector)
 */
export const activeLanguages = computed(() => {
    const active: Array<{ code: string; name: string; languageId: number }> = []
    for (const [code, enabled] of Object.entries(supportedLanguages.value)) {
        if (enabled && LANGUAGE_MAP[code]) {
            const name = (i18n.global as any).t(`languages.${code}`)
            active.push({ code, name, languageId: LANGUAGE_MAP[code] })
        }
    }
    // Always include default language
    if (active.length === 0) {
        const code = currentLanguage.value
        active.push({
            code,
            name: code.toUpperCase(),
            languageId: LANGUAGE_MAP[code] ?? 3
        })
    }
    return active
})

/**
 * Set language — updates i18n locale, languageIdBus, and localStorage
 */
export const LANG_STORAGE_KEY = 'user_lang_choice'

/**
 * Default language id used when no user preference is stored.
 * FR = 3 matches the backend default; backend also falls back to FR
 * when a description row is missing for the requested language.
 */
export const DEFAULT_LANGUAGE_ID = 3

/**
 * Read the user's saved language preference synchronously and map it to a
 * language_id. Used before i18n is initialized (e.g. on the initial
 * getStore() call) so the backend can return content in the right language.
 */
export function getInitialLanguageId(): number {
    if (typeof localStorage === 'undefined') return DEFAULT_LANGUAGE_ID
    const savedLang = localStorage.getItem(LANG_STORAGE_KEY)
    if (savedLang && LANGUAGE_MAP[savedLang]) {
        return LANGUAGE_MAP[savedLang]
    }
    return DEFAULT_LANGUAGE_ID
}

function applyLanguage(code: string) {
    currentLanguage.value = code
    i18n.global.locale.value = code as any
    languageIdBus.value = LANGUAGE_MAP[code] ?? 3
    if (typeof document !== 'undefined') {
        document.documentElement.lang = code
    }
}

/**
 * User-triggered language change (from dropdown).
 * Persists choice to localStorage.
 */
export function setLanguage(code: string) {
    applyLanguage(code)
    localStorage.setItem(LANG_STORAGE_KEY, code)
}

/**
 * Initialize locale from backend settings
 * Called from getStore() after settings are loaded
 */
export function initLocale(
    settings: Array<{ key: string; value: string }>,
    storeData?: {
        currency?: {
            code?: string
            title?: string
            symbol_left?: string | null
            symbol_right?: string | null
            decimal_place?: number
        }
    }
) {
    // Set currency info from backend (preferred - real DB values)
    if (storeData?.currency) {
        currencyInfo.value = storeData.currency
    }

    // Supported languages FIRST so we can validate saved language against it
    const selectedLangs = settings.find(s => s.key === 'selectedLanguages')
    if (selectedLangs?.value) {
        try {
            const parsed = typeof selectedLangs.value === 'string'
                ? JSON.parse(selectedLangs.value)
                : selectedLangs.value
            supportedLanguages.value = parsed
        } catch {
            supportedLanguages.value = {}
        }
    }

    // Default language
    const configLang = settings.find(s => s.key === 'config_language')
    const defaultLang = configLang?.value

    if (defaultLang) {
        // Clean up old localStorage key from previous versions
        localStorage.removeItem('language')

        const savedLang = localStorage.getItem(LANG_STORAGE_KEY)
        // Only use savedLang if user explicitly chose it AND it's in the store's supported languages
        const isSavedLangSupported =
            savedLang &&
            LANGUAGE_MAP[savedLang] &&
            supportedLanguages.value[savedLang] === true

        if (isSavedLangSupported) {
            applyLanguage(savedLang!)
        } else {
            // Clear stale localStorage and use store default (no localStorage write)
            localStorage.removeItem(LANG_STORAGE_KEY)
            applyLanguage(defaultLang)
        }
    }

    // Currency
    const configCurrency = settings.find(s => s.key === 'config_currency')
    if (configCurrency?.value) {
        currentCurrency.value = configCurrency.value
    }
}
